import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import EditIcon from '@mui/icons-material/Edit'
import QrCodeIcon from '@mui/icons-material/QrCode'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CreateSiteDialog from '../CreateSiteDialog'
import { AdminSitesDocument, SiteRole } from '../gql/graphql'
import { useSiteContext } from '../hooks/useSiteContext'

gql`
  query AdminSites {
    adminSites {
      id
      name
      location
      images
      courts {
        id
        name
        displayOrder
      }
    }
  }
`

export default function SitesPage() {
  const navigate = useNavigate()
  const { sites: siteContextSites } = useSiteContext()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [qrDialogOpen, setQrDialogOpen] = useState(false)
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null)
  const [qrLoading, setQrLoading] = useState(false)
  const { data, loading, error } = useQuery(AdminSitesDocument, {
    fetchPolicy: 'network-only',
  })

  const handleCreateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur()
    setCreateDialogOpen(true)
  }

  const handleQrClick = async (siteId: string) => {
    setQrLoading(true)
    setQrDialogOpen(true)
    setQrImageUrl(null)

    try {
      const token = sessionStorage.getItem('authToken')
      const response = await fetch(`/api/siteqr?site_id=${siteId}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })

      if (!response.ok) {
        throw new Error(
          `Failed to fetch QR code: ${response.status} ${response.statusText}`,
        )
      }

      const contentType = response.headers.get('content-type')

      if (!contentType?.startsWith('image/png')) {
        const text = await response.text()
        console.error('Unexpected response:', text)
        throw new Error('Response is not a PNG image')
      }

      const blob = await response.blob()

      if (blob.size === 0) {
        throw new Error('Received empty image')
      }

      const url = URL.createObjectURL(blob)
      setQrImageUrl(url)
    } catch (err) {
      console.error('Error fetching QR code:', err)
      alert(`获取二维码失败: ${err instanceof Error ? err.message : '未知错误'}`)
      setQrDialogOpen(false)
    } finally {
      setQrLoading(false)
    }
  }

  const handleQrDialogClose = () => {
    setQrDialogOpen(false)
    if (qrImageUrl) {
      URL.revokeObjectURL(qrImageUrl)
      setQrImageUrl(null)
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            场馆管理
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddBusinessIcon />}
          onClick={handleCreateClick}
        >
          创建场馆
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">加载场馆列表失败: {error.message}</Alert>}

      {data && data.adminSites.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            您还没有关联任何场馆
          </Typography>
          <Typography variant="body2">您可以：</Typography>
          <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>点击右上角"创建场馆"按钮创建新场馆</li>
            <li>或联系场馆管理员将您添加为管理员</li>
          </Typography>
        </Alert>
      )}

      {data && data.adminSites.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.adminSites.map((site) => (
            <Card key={site.id}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {site.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {site.location}
                    </Typography>
                    <Typography variant="body2">
                      场地数量: {site.courts.length} 个
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {siteContextSites.find((s) => s.id === site.id)?.role ===
                      SiteRole.SuperAdmin && (
                      <IconButton
                        onClick={() => navigate(`/site/${site.id}/edit`)}
                        color="primary"
                        title="编辑场馆"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => handleQrClick(site.id)}
                      color="primary"
                      title="下载场馆二维码"
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <CreateSiteDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      <Dialog
        open={qrDialogOpen}
        onClose={handleQrDialogClose}
        sx={{
          '& .MuiDialog-paper': { width: 350 },
        }}
      >
        <DialogTitle>场馆二维码</DialogTitle>
        <DialogContent dividers>
          {qrLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {qrImageUrl && !qrLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
              }}
            >
              <Box
                component="img"
                src={qrImageUrl}
                alt="Site QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  borderRadius: 2,
                  boxShadow: 3,
                }}
                onError={(e) => {
                  console.error('Image failed to load:', e)
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
